import axios from 'axios';
import getAuthSession from './auth';
import { FetchAllWorkflowsRequest } from '../app/store/workflowSlice';
import { Workflow, WorkflowTypeKey } from '../interfaces/workflow.interface';
import { PaginatedResponse } from './assessments';
import { Questionnaire, RemediationValidationAdminRow } from '../interfaces/questionnaire.interface';

export const WORKFLOW_STATUSES = {
  INCOMPLETE: 'incomplete',
  NON_COMPLIANT: 'non-compliant',
  COMPLETE: 'complete',
  SUBMITTED_FOR_ASSESSMENT: 'submittedForAssessment',
  ASSIGNED: 'Assigned',
  PRE_ASSESSMENT: 'Pre-Assessment',
  ASSESSING: 'Assessing',
  SUBMITTED: 'Submitted',
  REJECTED: 'Rejected',
  REMEDIATION: 'Remediation',
  FINAL_COMPLETE: 'Complete',
} as const;

export const wfHierarchy = [
  WORKFLOW_STATUSES.INCOMPLETE,
  WORKFLOW_STATUSES.NON_COMPLIANT,
  WORKFLOW_STATUSES.COMPLETE,
  WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT,
  WORKFLOW_STATUSES.ASSIGNED,
  WORKFLOW_STATUSES.PRE_ASSESSMENT,
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REJECTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
];

export type WorkflowStatus = typeof WORKFLOW_STATUSES[keyof typeof WORKFLOW_STATUSES];

export const PRE_ASSESSMENT_STATUSES: WorkflowStatus[] = [
  WORKFLOW_STATUSES.PRE_ASSESSMENT,
];

export type AssessmentWfStatusType =
  | typeof WORKFLOW_STATUSES.ASSESSING
  | typeof WORKFLOW_STATUSES.SUBMITTED
  | typeof WORKFLOW_STATUSES.REMEDIATION
  | typeof WORKFLOW_STATUSES.FINAL_COMPLETE;

export const assessmentWfStatuses: Set<AssessmentWfStatusType> = new Set([
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
]);

export function isAssessmentWfStatus(val: string): boolean {
  return assessmentWfStatuses.has(val as AssessmentWfStatusType);
}

// Keep the first workflow per id (callers spread active first) so a just-deactivated workflow briefly in both the active and inactive lists doesn't render twice.
export function dedupeWorkflowsById(workflows: Workflow[]): Workflow[] {
  const seen = new Map<number, Workflow>();
  for (const workflow of workflows) {
    if (!seen.has(workflow.id)) seen.set(workflow.id, workflow);
  }
  return [...seen.values()];
}

export async function createNewWorkflow(props: { workflowType: WorkflowTypeKey, baseline: number, serviceProvider: number, status: string, site?: number, application?: number, applicationVersion?: number, version?: number }) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/workflows/`,
    { ...props, isActive: true },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function fetchWorkflowById(workflow_id: number) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/workflows/${workflow_id}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as Workflow;
}

export async function fetchWorkflows(request: FetchAllWorkflowsRequest, isActive?: boolean) {
  const authSession = await getAuthSession();

  const queryString = Object.entries(request)
    .filter(([, val]) => val !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const activeParam =
    typeof isActive !== 'undefined' ? `&is_active=${isActive}` : '';

  const url = `${process.env.REACT_APP_BASE_API}/workflows/?limit=100${activeParam}${
    queryString ? `&${queryString}` : ''
  }`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
    },
  });

  return response.data.results as Workflow[];
}

export async function getVerificationRemediationsByWorkflow(workflowId: number): Promise<PaginatedResponse<RemediationValidationAdminRow>> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/verify-workflow-remediation-items/${workflowId}/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function deactivateWorkflowProcess(workflow_id: number) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/workflows/deactivate/`,
    {
      workflow_id,
    },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function deactivateActiveWorkflows(activeWorkflowIds: number[]) {
  const responses = await Promise.all(activeWorkflowIds.map(async (id) => {
    return deactivateWorkflowProcess(id);
  }));
  return responses;
}

export const handleDeativateWorkflows = async (workflows: Workflow[]) => {
  if (!workflows || workflows.length === 0) return;

  const activeWorkflowIds: number[] = workflows.filter(wf => wf.isActive).map(wf => wf.id);
  await deactivateActiveWorkflows(activeWorkflowIds);
};

export async function updateWorkflow(request: Partial<Workflow>) {
  const authSession = await getAuthSession();
  const { id,  ...req } = request;
  const currentWorkflow = await axios.get(
    `${process.env.REACT_APP_BASE_API}/workflows/${id}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );

  const currentStatus = currentWorkflow.data?.status;

  const currentStatusIndex = wfHierarchy.indexOf(currentStatus || '');
  const newStatusIndex = wfHierarchy.indexOf(req.status as typeof wfHierarchy[number] || '');
  const statusExceptions = (currentStatus === 'complete' && req.status === 'incomplete') || (currentStatus === 'non-compliant' && req.status === 'incomplete');
  // If the new status is lower in the hierarchy, just use the current status unless in pre-assessment phase
  if (newStatusIndex < currentStatusIndex && !statusExceptions) {
    req.status = currentStatus;
  }
  const url = `${process.env.REACT_APP_BASE_API}/workflows/${id}/`;
  const response = await axios.patch(
    url, req,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as Workflow;
}

// Constants for workflow types
export const TPN_BEST_PRACTICE_5_1 = 'tpn_best_practices_5.1';
export const TPN_BEST_PRACTICE_5_2 = 'tpn_best_practices_5.2';
export const TPN_BEST_PRACTICE_5_3 = 'tpn_best_practices_5.3';
export const TPN_BEST_PRACTICE_5_3_1 = 'tpn_best_practices_5.3.1';

export interface WorkflowEntry {
  label: string;
  version: string;
  questionnaireTitle: string;
  questionnaireVersion: string;
  sortOrder: number;
}

export type WorkflowMap = {
  [key in typeof TPN_BEST_PRACTICE_5_1 | typeof TPN_BEST_PRACTICE_5_2 | typeof TPN_BEST_PRACTICE_5_3 | typeof TPN_BEST_PRACTICE_5_3_1]: WorkflowEntry;
};

export type WorkflowMapRecord = Record<string, WorkflowEntry>;

/** The version part of a workflow type, e.g. 'tpn_best_practices_5.3.1' -> '5.3.1'. */
export const workflowShortVersion = (workflowType: string): string =>
  workflowType.replace('tpn_best_practices_', '');

// Static seed for the versions that existed before questionnaires carried their own
// workflow values. Newer versions are registered dynamically from fetched questionnaires
// (see registerQuestionnaireWorkflows / useWorkflowMap).
export const WORKFLOW_MAP: WorkflowMap = {
  [TPN_BEST_PRACTICE_5_1]: {
    label: 'Questionnaire v5.1',
    version: 'v5.1',
    questionnaireVersion: '5.1.0',
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.1',
    sortOrder: 0,
  },
  [TPN_BEST_PRACTICE_5_2]: {
    label: 'Questionnaire v5.2',
    version: 'v5.2',
    questionnaireVersion: '5.2.0',
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.2',
    sortOrder: 1,
  },
  [TPN_BEST_PRACTICE_5_3]: {
    label: 'Questionnaire v5.3',
    version: 'v5.3',
    questionnaireVersion: '5.3.0',
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.3',
    sortOrder: 2,
  },
  [TPN_BEST_PRACTICE_5_3_1]: {
    label: 'Questionnaire v5.3.1',
    version: 'v5.3.1',
    questionnaireVersion: '5.3.1',
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.3.1',
    sortOrder: 3,
  },
};

// App-wide registry of workflow entries, seeded with the legacy static map and extended
// with entries built from questionnaires fetched from the API (which carry workflowType,
// workflowLabel and sortOrder). Subscribers re-render when new entries register.
const workflowMap: WorkflowMapRecord = { ...WORKFLOW_MAP };
const workflowMapSubscribers = new Set<(map: WorkflowMapRecord) => void>();

/** Register workflow entries for MPA BP questionnaires fetched from the API. */
export function registerQuestionnaireWorkflows(questionnaires: Questionnaire[]): void {
  let changed = false;
  questionnaires.forEach((questionnaire) => {
    const workflowType = questionnaire.workflowType;
    if (!workflowType) return;
    const entry: WorkflowEntry = {
      label: questionnaire.workflowLabel || `Questionnaire v${workflowShortVersion(workflowType)}`,
      version: `v${workflowShortVersion(workflowType)}`,
      questionnaireTitle: questionnaire.title,
      questionnaireVersion: questionnaire.version,
      sortOrder: questionnaire.sortOrder ?? 0,
    };
    if (JSON.stringify(workflowMap[workflowType]) !== JSON.stringify(entry)) {
      workflowMap[workflowType] = entry;
      changed = true;
    }
  });
  if (changed) workflowMapSubscribers.forEach((cb) => cb({ ...workflowMap }));
}

let workflowMapInflight: Promise<void> | null = null;

/** Fetch the MPA BP questionnaires once and register their workflow entries. */
export function refreshWorkflowMap(): Promise<void> {
  if (!workflowMapInflight) {
    workflowMapInflight = (async () => {
      const authSession = await getAuthSession();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API}/questionnaires/?type=mpa_best_practice&limit=200`,
        { headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` } },
      );
      registerQuestionnaireWorkflows(response.data.results as Questionnaire[]);
    })().catch((error) => { console.error('Failed to load workflow map', error); });
  }
  return workflowMapInflight;
}

/** Test-only: reset the dynamic registry back to the static seed. */
export function resetWorkflowMap(): void {
  Object.keys(workflowMap).forEach((key) => {
    if (!(key in WORKFLOW_MAP)) delete workflowMap[key];
  });
  Object.assign(workflowMap, WORKFLOW_MAP);
  workflowMapSubscribers.clear();
  workflowMapInflight = null;
}

export function subscribeToWorkflowMap(callback: (map: WorkflowMapRecord) => void): () => void {
  workflowMapSubscribers.add(callback);
  return () => workflowMapSubscribers.delete(callback);
}

export const getWorkflowMap = (): WorkflowMapRecord => ({ ...workflowMap });

export const workflowEntry = (workflowType?: string | null): WorkflowEntry | undefined =>
  workflowType ? workflowMap[workflowType] : undefined;

const compareVersionStrings = (a: string, b: string): number => {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i += 1) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

/** Workflow types known to the registry, oldest first (sortOrder, then version). */
export const getOrderedWorkflowTypes = (): string[] =>
  Object.entries(workflowMap)
    .sort(([, a], [, b]) =>
      (a.sortOrder - b.sortOrder) || compareVersionStrings(a.questionnaireVersion, b.questionnaireVersion))
    .map(([key]) => key);

/** The newest workflow type known to the registry. */
export const newestWorkflowType = (): string => {
  const ordered = getOrderedWorkflowTypes();
  return ordered[ordered.length - 1];
};

/** The workflow type that follows the given one; undefined when already newest or unknown. */
export const nextWorkflowType = (workflowType?: string): string | undefined => {
  const ordered = getOrderedWorkflowTypes();
  const index = workflowType ? ordered.indexOf(workflowType) : -1;
  return index >= 0 ? ordered[index + 1] : undefined;
};

/** Sort questionnaires newest first, by sortOrder then semantic version. */
export const sortQuestionnairesNewestFirst = <T extends { sortOrder?: number; version: string }>(
  questionnaires: T[],
): T[] =>
    [...questionnaires].sort((a, b) =>
      ((b.sortOrder ?? 0) - (a.sortOrder ?? 0)) || compareVersionStrings(b.version, a.version));

/**
 * Legacy ordered list of the static workflow types. Prefer getOrderedWorkflowTypes(),
 * which includes dynamically registered versions.
 */
export const WorkflowLabels = [
  TPN_BEST_PRACTICE_5_1,
  TPN_BEST_PRACTICE_5_2,
  TPN_BEST_PRACTICE_5_3,
  TPN_BEST_PRACTICE_5_3_1,
];

export const workflowLabels = (wf: string) => {
  return workflowEntry(wf)?.label || wf;
};

export const workflowVersion = (workflowType?: string) => {
  return workflowEntry(workflowType)?.version || '';
};

export const isWorkflow531OrHigher = (workflowType: string) => {
  return !(workflowType === TPN_BEST_PRACTICE_5_1 || workflowType === TPN_BEST_PRACTICE_5_2 || workflowType === TPN_BEST_PRACTICE_5_3);
};

/** True when a version-bearing string (e.g. 'Blue - v5.3.1 Expires ...') is 5.3.1 or higher. */
export const isVersionLabel531OrHigher = (label?: string): boolean => {
  const match = label?.match(/(\d+(?:\.\d+)+)/);
  return !!match && compareVersionStrings(match[1], '5.3.1') >= 0;
};

export const ReadOnlyBaselineWorkflowStatusArray: WorkflowStatus[] = [
  WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT,
  WORKFLOW_STATUSES.ASSIGNED,
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REJECTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
];

export const CanNotSetBackToIncompleteWorkflowStatusArray: WorkflowStatus[] = [
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REJECTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
];

// Assessment-related workflow actions
export async function assignAssessor(request: {
  assessorID: number;
  workflowId: number;
  assignedAssessorId: number;
  isOnPremise: boolean;
  isCloud: boolean;
  isInPerson: boolean;
}) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/assign-assessor/${request.workflowId}/`,
    { ...request },
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
}

export async function acceptAssessment(workflowId: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/accept-assessment/${workflowId}/`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
}

export async function beginAssessment(workflowId: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/begin-assessment/${workflowId}/`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
}

export async function rejectAssessment(workflowId: number) {
  const authSession = await getAuthSession();
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API}/reject-assessment/${workflowId}/`,
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
}

export async function updateAssessor(request: {
  assessorID: number;
  workflowId: number;
  assignedAssessorId: number;
  isOnPremise: boolean;
  isCloud: boolean;
  isInPerson: boolean;
}) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/update-assessor/${request.workflowId}/`,
    { ...request },
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
}

export async function submitAssessment(workflowId: number) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/submit-assessment/${workflowId}/`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data;
}
